/**
 * ClientSelector.js — 从 clients 集合搜索选择客户，自动填充关联字段
 *
 * 功能：
 * - 输入时搜索 clients（?search=），展示 company_name / abn / trading_name
 * - 选中后回调 onClientSelect(client) 让父组件填充字段
 * - 已关联客户时显示徽标 + 跳转链接
 * - 仍可手动输入（兼容非 CRM 客户）
 */

import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, X, ExternalLink, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientSelector({
  value,           // 当前 client_name 显示值
  clientId,        // 当前 client_id（如有）
  onChange,        // (value) => void  — 更新 client_name 文本
  onClientSelect,  // (client) => void — 选中完整客户对象
  onClear,         // () => void       — 清除关联
  placeholder = 'Search or type client name...',
  testId = 'client-selector',
}) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const searchTimer = useRef(null);

  // Click outside to close
  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search
  const handleInputChange = (text) => {
    onChange(text);
    setHighlightIndex(-1);

    if (searchTimer.current) clearTimeout(searchTimer.current);

    if (text.trim().length < 1) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    setIsOpen(true);
    setSearching(true);

    searchTimer.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/clients`, { params: { search: text.trim(), limit: 8 } });
        setResults(res.data?.data || []);
      } catch (err) {
        console.error('Client search failed:', err);
        setResults([]);
      }
      setSearching(false);
    }, 300);
  };

  const handleSelectClient = (client) => {
    onChange(client.company_name);
    onClientSelect?.(client);
    setIsOpen(false);
    setResults([]);
    inputRef.current?.blur();
  };

  const handleClear = () => {
    onClear?.();
    setResults([]);
    setIsOpen(false);
  };

  const handleKeyDown = (e) => {
    if (!isOpen || results.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex(prev => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && highlightIndex >= 0) {
      e.preventDefault();
      handleSelectClient(results[highlightIndex]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="flex items-center gap-1">
        <div className="relative flex-1">
          <input
            ref={inputRef}
            value={value || ''}
            onChange={e => handleInputChange(e.target.value)}
            onFocus={() => {
              if (value && value.trim().length >= 1 && results.length === 0) {
                // Trigger search on focus if there's a value
                handleInputChange(value);
              }
            }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="editable-field w-full pr-8"
            data-testid={testId}
          />
          {searching && (
            <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-[#c9a84c] animate-spin" />
          )}
        </div>

        {/* Linked client badge */}
        {clientId && (
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={() => navigate(`/clients/${clientId}`)}
              className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold transition-colors"
              style={{ background: '#1a1a2e', color: '#c9a84c' }}
              title="View client details"
              data-testid={`${testId}-link`}
            >
              <Building2 className="w-3 h-3" />
              Linked
              <ExternalLink className="w-3 h-3" />
            </button>
            <button
              onClick={handleClear}
              className="p-1 rounded hover:bg-red-50 text-[#6b7280] hover:text-[#dc2626] transition-colors"
              title="Unlink client"
              data-testid={`${testId}-unlink`}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>

      {/* Dropdown results */}
      {isOpen && results.length > 0 && (
        <div
          className="absolute z-50 left-0 right-0 mt-1 rounded-lg shadow-lg border max-h-64 overflow-y-auto"
          style={{ background: '#f8f7f4', borderColor: 'rgba(201,168,76,0.3)' }}
          data-testid={`${testId}-dropdown`}
        >
          {results.map((client, i) => (
            <div
              key={client.id}
              className="px-3 py-2.5 cursor-pointer transition-colors border-b last:border-b-0"
              style={{
                background: i === highlightIndex ? 'rgba(201,168,76,0.1)' : 'transparent',
                borderColor: 'rgba(201,168,76,0.08)',
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelectClient(client);
              }}
              onMouseEnter={() => setHighlightIndex(i)}
              data-testid={`${testId}-option-${i}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-[#1a1a2e]">
                  {client.company_name}
                </span>
                {client.client_no && (
                  <span className="text-xs font-mono text-[#6b7280]">{client.client_no}</span>
                )}
              </div>
              <div className="flex items-center gap-3 mt-0.5">
                {client.trading_name && (
                  <span className="text-xs text-[#6b7280]">{client.trading_name}</span>
                )}
                {client.abn && (
                  <span className="text-xs text-[#9ca3af]">ABN: {client.abn}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* No results message */}
      {isOpen && !searching && value && value.trim().length >= 1 && results.length === 0 && (
        <div
          className="absolute z-50 left-0 right-0 mt-1 rounded-lg shadow-lg border px-3 py-4 text-center"
          style={{ background: '#f8f7f4', borderColor: 'rgba(201,168,76,0.2)' }}
        >
          <p className="text-sm text-[#6b7280]">No clients found. Type to create a new client.</p>
        </div>
      )}
    </div>
  );
}
